/**
 * Animated octopus loader with headphones
 */

import { useState, useEffect, useMemo } from 'react';
import { Check } from 'lucide-react';

export type ProcessingStage = 'uploading' | 'chunking' | 'parallel_processing' | 'synthesizing' | null;

interface OctopusLoaderProps {
  message?: string;
  subMessage?: string;
  stage?: ProcessingStage;
  elapsedSeconds?: number;
  estimatedSeconds?: number | null;
  showAllStages?: boolean;
  rotatingMessages?: string[];
  messageInterval?: number;
}

const STAGES = [
  { id: 'uploading', label: 'Uploading to cloud storage' },
  { id: 'chunking', label: 'Splitting audio into chunks' },
  { id: 'parallel_processing', label: 'Transcribing & analyzing in parallel' },
  { id: 'synthesizing', label: 'AI synthesis with DeepSeek' },
] as const;

function getStageIndex(stage: ProcessingStage | undefined): number {
  if (!stage) return -1;
  return STAGES.findIndex(s => s.id === stage);
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
}

// Default fun messages for rotating display
const DEFAULT_FUN_MESSAGES = [
  "Feeling the rhythm...",
  "Dancing through the beats...",
  "Vibing with your music...",
  "Riding the sound waves...",
  "Catching the melody...",
  "Swimming through frequencies...",
  "Hugging the bass line...",
  "High-fiving the treble...",
  "Surfing sonic waves...",
  "Grooving in the deep...",
  "Letting the music flow...",
  "Channeling good vibes...",
  "Harmonizing with the universe...",
  "Floating on sound clouds...",
  "Jamming with the algorithms...",
  "Tickling the audio spectrum...",
  "Wrapping tentacles around the beat...",
  "Making friends with the frequencies...",
  "Splashing through soundscapes...",
  "Doing the octopus shuffle...",
  "Waving to the waveforms...",
  "Ink-redibly processing...",
  "Eight arms, infinite possibilities...",
  "Bubbling with creativity...",
  "Deep diving into your track...",
  "Tentacle-tapping to the tempo...",
  "Ocean of sound, here we come...",
  "Syncing with the sonic sea...",
  "Making waves in the best way...",
  "Gliding through golden frequencies...",
  "Feeling absolutely fin-tastic...",
  "Spinning up some magic...",
  "Orchestrating awesomeness...",
  "Conjuring creative vibes...",
  "Brewing something beautiful...",
  "Marinating in the music...",
  "Soaking up the sound...",
  "Absorbing all the good stuff...",
  "Percolating pure potential...",
  "Steeping in sonic goodness...",
  "Composing with care...",
  "Crafting with eight arms...",
  "BRB, consulting the musical kraken...",
  "Asking the wise coral for advice...",
  "Sending good vibes through the ocean...",
  "Currently obsessed with your track...",
  "This slaps harder than a whale tail...",
  "Doing a little underwater dance...",
  "My tentacles are tingling...",
  "The sea creatures approve...",
  "Even the dolphins are impressed...",
  "Squirting ink with excitement...",
  "Having a moment here...",
  "No cap, this is fire...",
  "Living my best octopus life...",
  "Can't stop won't stop vibing...",
  "This track hits different...",
  "Certified banger detected...",
  "Alexa, play this on repeat...",
  "Adding to my underwater playlist...",
  "The ocean currents are grooving...",
  "Tentacles = tingled...",
  "Chef's kiss from the deep...",
  "The abyss approves...",
  "Giving it the sucker stamp of approval...",
  "Making bubbles of joy...",
  "Wiggling with anticipation...",
  "The plankton are partying...",
  "Feeling some type of way...",
  "My hearts are all fluttering...",
  "Yes, I have three hearts. All happy...",
  "Ink-credible vibes detected...",
  "Swimming in pure serotonin...",
  "The jellyfish are jealous...",
  "Sending this to my squid friends...",
  "Currently in my happy place...",
  "The starfish gave it five stars...",
  "Doing the tentacle twirl...",
  "Bloop bloop bloop (that means good)...",
  "The seahorses are swaying...",
  "Activating maximum chill mode...",
  "This is my jam. Get it? Jam?...",
  "Kelp forest approved...",
  "The whales are singing along...",
  "Catching feelings and waves...",
  "My camouflage is turning happy colors...",
  "Processing with all eight brains...",
  "Yes, octopi have nine brains. True story...",
  "The shrimp are shimmying...",
  "Coral reef vibes activated...",
  "Doing science... but make it groovy...",
  "The clownfish aren't clowning around...",
  "This deserves a standing ovation...",
  "Well, a swimming ovation...",
  "Making memories in the deep blue...",
  "The turtles think it's totally rad...",
];

// Shuffle array helper
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export default function OctopusLoader({
  message = 'Processing...',
  subMessage,
  stage,
  elapsedSeconds,
  estimatedSeconds,
  showAllStages = false,
  rotatingMessages,
  messageInterval = 3000,
}: OctopusLoaderProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // Shuffle messages on mount for unpredictable fun
  const messages = useMemo(() => {
    const msgs = rotatingMessages || DEFAULT_FUN_MESSAGES;
    return shuffleArray(msgs);
  }, [rotatingMessages]);

  // Rotate through messages
  useEffect(() => {
    if (messages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
    }, messageInterval);

    return () => clearInterval(interval);
  }, [messages.length, messageInterval]);

  // If showAllStages is true and no stage is set yet, default to first stage (uploading)
  const effectiveStage = stage ?? (showAllStages ? 'uploading' : null);
  const currentStageIndex = getStageIndex(effectiveStage);
  const shouldShowStages = showAllStages || stage;

  // Calculate time remaining
  const timeRemaining = estimatedSeconds && elapsedSeconds !== undefined
    ? Math.max(0, estimatedSeconds - elapsedSeconds)
    : null;

  // Use rotating message as subMessage if no explicit subMessage and no stages shown
  const displaySubMessage = subMessage || (!shouldShowStages ? messages[currentMessageIndex] : undefined);

  return (
    <div className="flex flex-col items-center py-4">
      {/* Timer with estimate */}
      {elapsedSeconds !== undefined && elapsedSeconds > 0 && (
        <div className="mb-2 px-3 py-1 bg-zinc-800 rounded-full flex items-center gap-2">
          <span className={`text-xs font-mono ${timeRemaining !== null && timeRemaining <= 0 ? 'text-yellow-400' : 'text-jelly-400'}`}>
            {formatTime(elapsedSeconds)}
          </span>
          {estimatedSeconds && (
            <>
              <span className="text-zinc-600">/</span>
              <span className={`text-xs font-mono ${timeRemaining !== null && timeRemaining <= 0 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                ~{formatTime(estimatedSeconds)}
              </span>
            </>
          )}
        </div>
      )}

      {/* Octopus Container */}
      <div className="relative w-32 h-32">
        {/* Spinning ring */}
        <div className="absolute inset-0 rounded-full border-4 border-zinc-800" />
        <div className="absolute inset-0 rounded-full border-4 border-jelly-500 border-t-transparent animate-spin" />

        {/* Octopus */}
        <div className="absolute inset-2 flex items-center justify-center">
          <div className="octopus-bounce">
            <svg viewBox="0 0 100 100" className="w-20 h-20">
              {/* Headphones band */}
              <path
                d="M25 35 Q50 15 75 35"
                fill="none"
                stroke="#333"
                strokeWidth="4"
                strokeLinecap="round"
              />

              {/* Left ear cup */}
              <rect x="18" y="32" width="12" height="18" rx="3" fill="#444" className="animate-pulse-left" />
              <rect x="21" y="36" width="6" height="10" rx="2" fill="#666" />

              {/* Right ear cup */}
              <rect x="70" y="32" width="12" height="18" rx="3" fill="#444" className="animate-pulse-right" />
              <rect x="73" y="36" width="6" height="10" rx="2" fill="#666" />

              {/* Head */}
              <ellipse cx="50" cy="45" rx="22" ry="20" fill="url(#octopusGradient)" />

              {/* Eyes */}
              <ellipse cx="42" cy="43" rx="5" ry="6" fill="white" />
              <ellipse cx="58" cy="43" rx="5" ry="6" fill="white" />
              <circle cx="43" cy="44" r="2.5" fill="#1a1a2e" className="animate-look" />
              <circle cx="59" cy="44" r="2.5" fill="#1a1a2e" className="animate-look" />

              {/* Smile */}
              <path
                d="M44 52 Q50 57 56 52"
                fill="none"
                stroke="#1a1a2e"
                strokeWidth="2"
                strokeLinecap="round"
              />

              {/* Tentacles */}
              <g className="tentacles">
                <path d="M32 62 Q30 75 28 82" stroke="url(#tentacleGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-1" />
                <path d="M40 64 Q38 77 36 85" stroke="url(#tentacleGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-2" />
                <path d="M50 65 Q50 78 50 88" stroke="url(#tentacleGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-3" />
                <path d="M60 64 Q62 77 64 85" stroke="url(#tentacleGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-4" />
                <path d="M68 62 Q70 75 72 82" stroke="url(#tentacleGradient)" strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-5" />
              </g>

              {/* Gradients */}
              <defs>
                <linearGradient id="octopusGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#e84a8a" />
                  <stop offset="100%" stopColor="#d63384" />
                </linearGradient>
                <linearGradient id="tentacleGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#d63384" />
                  <stop offset="100%" stopColor="#e84a8a" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* Sound wave indicators */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 flex gap-0.5">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="w-1 bg-jelly-500 rounded-full animate-soundwave"
              style={{
                height: `${8 + i * 4}px`,
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 flex gap-0.5">
          {[3, 2, 1].map((i) => (
            <div
              key={i}
              className="w-1 bg-jelly-500 rounded-full animate-soundwave"
              style={{
                height: `${8 + i * 4}px`,
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Message */}
      <p className="text-sm text-zinc-300 mt-4 mb-1">{message}</p>
      {displaySubMessage && !shouldShowStages && (
        <p className="text-xs text-zinc-500 text-center px-4 transition-opacity duration-500 animate-fade-in">
          {displaySubMessage}
        </p>
      )}

      {/* Stage progress */}
      {shouldShowStages && (
        <div className="mt-4 space-y-2.5 w-full max-w-xs">
          {STAGES.map((s, index) => {
            const isComplete = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;
            const isPending = index > currentStageIndex;

            return (
              <div
                key={s.id}
                className={`flex items-center gap-3 text-xs transition-all ${
                  isComplete ? 'text-green-400' :
                  isCurrent ? 'text-jelly-400' :
                  'text-zinc-500'
                }`}
              >
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border ${
                  isComplete ? 'bg-green-500/20 border-green-500/50' :
                  isCurrent ? 'bg-jelly-500/20 border-jelly-500/50 animate-pulse' :
                  'bg-zinc-800/50 border-zinc-700'
                }`}>
                  {isComplete ? (
                    <Check className="w-3 h-3" />
                  ) : isCurrent ? (
                    <div className="w-2 h-2 bg-jelly-400 rounded-full" />
                  ) : (
                    <span className="text-[10px] text-zinc-600">{index + 1}</span>
                  )}
                </div>
                <span className={isPending ? 'opacity-60' : ''}>{s.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Inline styles for animations */}
      <style>{`
        .octopus-bounce {
          animation: bounce 1s ease-in-out infinite;
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }

        .animate-look {
          animation: look 3s ease-in-out infinite;
        }

        @keyframes look {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(2px); }
          75% { transform: translateX(-2px); }
        }

        .animate-pulse-left {
          animation: pulseLeft 1s ease-in-out infinite;
        }

        .animate-pulse-right {
          animation: pulseRight 1s ease-in-out infinite;
        }

        @keyframes pulseLeft {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 transparent); }
          50% { transform: scale(1.05); filter: drop-shadow(-2px 0 4px rgba(232, 74, 138, 0.3)); }
        }

        @keyframes pulseRight {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 transparent); }
          50% { transform: scale(1.05); filter: drop-shadow(2px 0 4px rgba(232, 74, 138, 0.3)); }
        }

        .tentacle-1 { animation: wave 1.2s ease-in-out infinite; animation-delay: 0s; }
        .tentacle-2 { animation: wave 1.2s ease-in-out infinite; animation-delay: 0.1s; }
        .tentacle-3 { animation: wave 1.2s ease-in-out infinite; animation-delay: 0.2s; }
        .tentacle-4 { animation: wave 1.2s ease-in-out infinite; animation-delay: 0.3s; }
        .tentacle-5 { animation: wave 1.2s ease-in-out infinite; animation-delay: 0.4s; }

        @keyframes wave {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }

        .animate-soundwave {
          animation: soundwave 0.8s ease-in-out infinite;
        }

        @keyframes soundwave {
          0%, 100% { transform: scaleY(1); opacity: 0.6; }
          50% { transform: scaleY(1.5); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
